import React from "react";
import type { StudyTypeIconDescriptor } from "../../transformer";

interface StudyTypeIconProps {
	/**
	 * Icon descriptor produced by `processIconPath` / `processIconPath2`.
	 * If null/undefined, the component renders nothing.
	 */
	descriptor?: StudyTypeIconDescriptor | null;
	/**
	 * Optional color override. Defaults to the color baked into the descriptor.
	 */
	color?: string;
	/**
	 * Rendered size in pixels (applies to both width and height for material
	 * symbols and to the wrapping <img>/<i> element for custom SVGs).
	 */
	size?: number;
	className?: string;
	style?: React.CSSProperties;
	alt?: string;
}

const StudyTypeIcon: React.FC<StudyTypeIconProps> = ({ descriptor, color, size, className, style, alt = "" }) => {
	if (!descriptor) return null;

	const resolvedColor = color ?? descriptor.color;
	const resolvedSize = size ?? descriptor.size;

	if (descriptor.type === "material") {
		return (
			<i
				className={className ? `material-symbols-outlined ${className}` : "material-symbols-outlined"}
				style={{
					color: resolvedColor,
					fontSize: resolvedSize,
					...style,
				}}
				role={alt ? "img" : undefined}
				aria-hidden={alt ? undefined : true}
				aria-label={alt || undefined}>
				{descriptor.value}
			</i>
		);
	}

	// Custom SVG asset. The SVGs ship with their own brand fills, so `color` is
	// not applied via CSS (would require `currentColor` in the source SVG).
	return (
		<img
			src={descriptor.value}
			alt={alt}
			className={className}
			style={{
				width: resolvedSize,
				height: resolvedSize,
				...style,
			}}
		/>
	);
};

export default StudyTypeIcon;
